import { call, delay, put, select } from 'redux-saga/effects';
import AppUtils from '@control-front-end/utils/utils';
import { Utils } from 'mw-style-react';
import {
  UPDATE_ACTOR,
  NON_FILTER_KEYS,
} from '@control-front-end/common/constants/graphActors';

/**
 * Аватар актора
 */
function makeActorPictureBase(model, config) {
  let pictureUrl;
  if (model.systemObjType === 'user' || model.type === 'user') {
    pictureUrl = config.avatarPath.replace(
      '{userId}',
      model.systemObjId || model.id
    );
  } else if (model.picture && !model.picture.includes('static')) {
    pictureUrl = AppUtils.makeAppUrl(`/download/${model.picture}`);
  } else if (model.isLayer && model.pictureUrl) {
    pictureUrl = model.pictureUrl;
  } else {
    const picture = model.formPicture || model.picture;
    pictureUrl = picture && picture.includes('static') ? picture : null;
  }
  return pictureUrl;
}

/**
 * Аватар актора генератор
 */
export function* makeActorPicture(model) {
  const config = yield select((state) => state.config);
  return makeActorPictureBase(model, config);
}

/**
 * Добавить аватар пользователю
 */
export function makeUserWithAvatar(model, config) {
  const userId = model.userId || model.id;
  const modelCopy = structuredClone(model);
  return {
    ...modelCopy,
    avatar: config.avatarPath.replace('{userId}', userId),
  };
}

export function makePictureObject(model) {
  if (!model.pictureObject) return;
  const copyPictureObject = { ...model.pictureObject };
  copyPictureObject.img =
    copyPictureObject.type === 'image'
      ? AppUtils.makeAppUrl(`/download/${copyPictureObject.img}`)
      : copyPictureObject.img;
  return copyPictureObject;
}

/**
 * Расширить модель ссылками на изображения (аватар актора, пользователей)
 */
export function* extendModelWithAvatars(model) {
  const config = yield select((state) => state.config);
  model.pictureUrl = yield call(makeActorPicture, model);
  model.access = (model.access || []).map((u) => makeUserWithAvatar(u, config));
  if (model.user) {
    model.user = makeUserWithAvatar(model.user, config);
  }
  if (model.attachments && model.attachments.length) {
    for (const attach of model.attachments) {
      attach.userAvatar = config.avatarPath.replace('{userId}', attach.userId);
    }
  }
  model.copyPictureObject = makePictureObject(model);
}

/**
 * Создать модель графа
 */
export function* makeGraphModels({
  nodes,
  edges,
  replaceAll = false,
  isTree = false,
}) {
  const config = yield select((state) => state.config);
  const systemForms = yield select((state) => state.systemForms);
  const eventFormId = systemForms.events.id;
  const makeTitle = (node) => {
    if (node.accessDenied) return 'Access Denied';
    if (node.title) return node.title;
    if (node.name) return node.name;
    const props = Object.keys(node.data);
    return node.data[props[0]];
  };
  const nodeModels = [];
  const edgeModels = [];
  const duplicatesObj = {};
  for (const i of nodes) {
    const privs = i.privs || {};
    const id = i.laId ? `node_${i.laId}` : i.id.toString();
    duplicatesObj[i.id] = id;
    if (!i.areaPicture || i.areaPicture === '') delete i.areaPicture;
    const copyPictureObject = makePictureObject(i);
    const pictureUrl = makeActorPictureBase(i, config);
    const access = (i.access || []).map((u) => makeUserWithAvatar(u, config));
    const user = i.user ? makeUserWithAvatar(i.user, config) : null;
    const classes = [];
    if (i.accessDenied) classes.push('accessDenied');
    if (i.pictureObject) classes.push('pictureObject');
    const isLayerArea = !!(i.formId === eventFormId && i.areaPicture);
    nodeModels.push({
      id,
      replaceAll,
      data: {
        ...i,
        id,
        actorId: i.actorId || i.id.toString(),
        group: 'nodes',
        type: 'node',
        title: makeTitle(i),
        user,
        access,
        pictureUrl,
        copyPictureObject,
        readOnly: isLayerArea || !privs.modify,
        accessDenied: isLayerArea || i.accessDenied,
        isLayerArea,
      },
      isLayerArea,
      locked: isLayerArea,
      status: 'new',
      position: i.position,
      classes,
    });
  }
  for (const i of edges) {
    const id = i.laId ? `edge_${i.laId}` : i.id.toString();
    const source = duplicatesObj[i.source] || i.source;
    const target = duplicatesObj[i.target] || i.target;
    if (i.laId && !i.laIdSource && !i.laIdTarget) {
      i.laIdSource = parseInt(source.split('_')[1], 10);
      i.laIdTarget = parseInt(target.split('_')[1], 10);
    }
    edgeModels.push({
      id,
      replaceAll,
      data: {
        ...i,
        id,
        edgeId: i.edgeId || i.id.toString(),
        group: 'edges',
        type: 'edge',
        sourceActorId: i.sourceActorId || i.source,
        targetActorId: i.targetActorId || i.target,
        source: i.laIdSource ? `node_${i.laIdSource}` : source,
        target: i.laIdTarget ? `node_${i.laIdTarget}` : target,
        name: i.name || '',
        targetArrow: isTree ? 'triangle' : 'none',
      },
      status: 'new',
    });
  }
  return { nodes: nodeModels, edges: edgeModels };
}

/**
 * Получить слой по id
 */
export function* getLayerById(id) {
  const graphLayers = yield select((state) => state.graphLayers);
  const { list } = graphLayers;
  return list.find((i) => i.id === id);
}

/**
 * Получить активный слой графа c акторами
 */
export function* getActiveLayer() {
  const graphLayers = yield select((state) => state.graphLayers);
  const { active } = graphLayers;
  return yield getLayerById(active);
}

/**
 * Изменяем граф активного слоя
 */
export function* sendReducerMsg({ type, payload, layerId }) {
  const graphLayers = yield select((state) => state.graphLayers);
  const { active, list } = graphLayers;
  const copyList = list.slice();
  const findLayerId = layerId || active;
  const layerIndex = copyList.findIndex((i) => i.id === findLayerId);
  if (layerIndex === -1) return null;
  const layer = copyList[layerIndex];
  const copyLayer = { ...layer, ...payload };
  copyList.splice(layerIndex, 1, copyLayer);
  yield put({
    type,
    payload: { list: copyList },
  });
  return layer;
}

/**
 * Найти актор по laId (node_122, edge_322)
 */
export function* getActorByGraphId(laId, type) {
  let typeList = 'graphLayers';
  let layer = yield getActiveLayer();
  const actorsList = yield select((state) => state.actorsList);
  if (!layer) {
    typeList = 'actorsList';
    layer = { [type]: actorsList.list };
  }
  if (AppUtils.isUuid(laId)) {
    const elUuid = layer[type].find(
      (i) =>
        i.id === laId ||
        (i.data && i.data.actorId === laId) ||
        (i.data && i.data.edgeId === laId)
    );
    return typeList === 'graphLayers' ? elUuid.data : elUuid;
  }
  const id = laId.toString().includes('_') ? laId.split('_')[1] : laId;
  if (!id) return null;
  const el = layer[type].find((i) => +i.data.laId === +id);
  return typeList === 'graphLayers' ? el.data : el;
}

/**
 * Получить элементы графа без статуса
 */
export function* getGraphEls(type, layerId) {
  const graphLayers = yield select((state) => state.graphLayers);
  const { active, list } = graphLayers;
  const findLayerId = layerId || active;
  const layer = list.find((i) => i.id === findLayerId);
  if (!layer || !layer[type]) return [];
  let filtered = layer[type].filter((i) => i.status !== 'removed');
  filtered = filtered.map((i) => {
    const copy = { ...i };
    delete copy.status;
    delete copy.replaceAll;
    return copy;
  });
  return structuredClone(filtered);
}

/**
 * Получить элементы графа и установить всем статус new
 */
export function* getAllGraphEls(type) {
  const graphLayers = yield select((state) => state.graphLayers);
  const { active, list } = graphLayers;
  const layer = list.find((i) => i.id === active);
  if (!layer) return [];
  const filtered = layer[type].filter((i) => i.status !== 'removed');
  filtered.forEach((i) => {
    i.status = 'new';
  });
  return filtered;
}

/**
 * Обновить свойство актора
 */
export function* updateActorProp({
  type,
  layerId,
  id,
  actorId,
  propId,
  value,
}) {
  const copyNodes = yield call(getGraphEls, 'nodes', layerId);
  const copyEdges = yield call(getGraphEls, 'edges', layerId);
  const actorsIndexes = id
    ? AppUtils.findAllIndexes(copyNodes, (i) => i.id === id)
    : AppUtils.findAllIndexes(copyNodes, (i) => i.data.actorId === actorId);
  for (const nodeIndex of actorsIndexes) {
    const node = copyNodes[nodeIndex];
    if (!node) continue;
    node.data[propId] = value;
    node.status = 'updated';
    copyNodes.splice(nodeIndex, 1, node);
  }
  yield sendReducerMsg({
    layerId,
    type,
    payload: {
      nodes: copyNodes,
      edges: copyEdges,
    },
  });
}

/**
 * Поменять св-ва элемента графа
 */
export function updateElProps(list, id, props, data = {}) {
  const index = list.findIndex((i) => i.id === id);
  if (index === -1) return null;
  const graphEl = list[index];
  const newData = { ...graphEl.data, ...data };
  const newEl = { ...graphEl, ...props, data: newData };
  list.splice(index, 1, newEl);
  return graphEl;
}

/**
 * Обновить свойства для всех копий актора
 */
export function updateActorsElProps({
  actorId,
  edgeId,
  list,
  props,
  data = {},
}) {
  const els = actorId
    ? list.filter((i) => i.data.actorId === actorId)
    : list.filter((i) => i.data.edgeId === edgeId);
  const graphEls = [];
  for (const el of els) {
    props.id = el.id;
    data.id = el.id;
    data.laId = el.data.laId;
    const i = updateElProps(list, el.id, props, data);
    if (i) graphEls.push(i);
  }
  return graphEls;
}

/**
 * Обновить модель актора на графе
 */
export function* updateActorOnGraph({ actorModel, layerId }) {
  const copyNodes = yield call(getGraphEls, 'nodes', layerId);
  const { nodes } = yield call(makeGraphModels, {
    nodes: [actorModel],
    edges: [],
  });
  const nodeModel = nodes[0];
  updateActorsElProps({
    actorId: nodeModel.data.actorId,
    list: copyNodes,
    props: { ...nodeModel, status: 'updated' },
    data: nodeModel.data,
  });
  return { nodes: copyNodes };
}

/**
 * Установить балансы акторам
 */
export function setActorsBalances({ balances, nodes }) {
  balances.forEach((item) => {
    const actors = nodes.filter(
      (node) => node.data.actorId === item.actorId && node.status !== 'removed'
    );
    for (const actor of actors) {
      if (item.accountsExist) {
        actor.data.balance = item.credit - item.debit;
        actor.data.balanceVector = item.vector;
      } else {
        delete actor.data.balanceVector;
        actor.data.balance = null;
      }
      actor.status = 'updated';
      delete actor.replaceAll;
    }
  });
}

/**
 * Сгруппировать акторы по шаблонам
 */
export function* groupActorsByTpl({ rootActorId, nodes, edges, edgeTypeId }) {
  const childrenEdges = edges.filter(
    (i) => i.data.sourceActorId === rootActorId
  );
  const childrenEdgesIds = childrenEdges.map((i) => i.id);
  const hiddenEdges = yield getGraphEls('hiddenEdges');
  if (!childrenEdges.length) return { groupEdges: edges, hiddenEdges };
  const childNodesIds = childrenEdges.map((i) => i.data.target);
  const targetToEdge = {};
  childrenEdges.forEach((i) => {
    targetToEdge[i.data.target] = i;
  });
  const childNodes = nodes.filter((i) => childNodesIds.includes(i.id));
  const groupChild = AppUtils.groupBy(childNodes, 'data.formId');
  for (const formId of Object.keys(groupChild)) {
    const groupActors = groupChild[formId];
    const groupNodeId = `${AppUtils.udid()}_${formId}_node`;
    const groupNode = {
      id: groupNodeId,
      data: {
        id: groupNodeId,
        actorId: groupNodeId,
        group: 'nodes',
        type: 'node',
        title: groupActors[0].data.formTitle,
        data: {},
        privs: {},
        readOnly: true,
        collapsed: true,
        isMore: true,
        isGroupedActor: true,
        color: groupActors[0].data.color,
      },
      status: 'new',
    };
    const groupEdgeId = `${AppUtils.udid()}_${formId}_edge`;
    const groupEdge = {
      id: groupEdgeId,
      data: {
        id: groupEdgeId,
        edgeId: groupEdgeId,
        group: 'edges',
        type: 'edge',
        sourceActorId: rootActorId,
        targetActorId: groupNodeId,
        source: rootActorId,
        target: groupNodeId,
        edgeTypeId,
        privs: {},
        isMore: true,
        name: '',
        targetArrow: 'triangle',
      },
      status: 'new',
    };
    nodes.push(groupNode);
    edges.push(groupEdge);
    for (const actor of groupActors) {
      const subEdge = { ...targetToEdge[actor.id] };
      subEdge.data.realSourceActorId = rootActorId;
      subEdge.data.sourceActorId = groupNodeId;
      subEdge.data.source = groupNodeId;
      edges.push(subEdge);
    }
  }
  const groupEdges = edges.filter((i) => !hiddenEdges.includes(i.id));
  return { groupEdges, hiddenEdges: childrenEdgesIds };
}

/**
 * Свернуть акторы в актор More
 */
export function* collapseNodesToMore({
  rootActorId,
  nodes,
  edges,
  edgeTypeId,
  level = 0,
}) {
  const maxCount = 3;
  const childNodesIds = [];
  const nodeToEdge = {};
  for (const i of edges) {
    if (i.data.sourceActorId !== rootActorId) continue;
    childNodesIds.push(i.data.target);
    nodeToEdge[i.data.target] = i;
  }
  const childNodes = [];
  for (const i of nodes) {
    if (childNodesIds.includes(i.id)) childNodes.push(i);
  }
  const sortChildNodes = AppUtils.sort(childNodes, 'data.updatedAt', 'desc');
  const visibleNodes = sortChildNodes.slice(0, maxCount);
  const hiddenNodes = sortChildNodes.slice(maxCount);
  const nextNodes = visibleNodes.map((i) => i.id);
  if (hiddenNodes.length) {
    const moreNodeId = `${AppUtils.udid()}_nodeMore`;
    const moreNode = {
      id: moreNodeId,
      data: {
        id: moreNodeId,
        actorId: moreNodeId,
        group: 'nodes',
        type: 'node',
        title: 'More...',
        data: {},
        privs: {},
        readOnly: true,
        collapsed: true,
        isMore: true,
        gradient: {
          type: 'linear-gradient',
          colors: '#FFFFFF #FFD68E',
        },
      },
      status: 'new',
    };
    const moreEdgeId = `${AppUtils.udid()}_edgeMore`;
    const moreEdge = {
      id: moreEdgeId,
      data: {
        id: moreEdgeId,
        edgeId: moreEdgeId,
        group: 'edges',
        type: 'edge',
        sourceActorId: rootActorId,
        targetActorId: moreNodeId,
        source: rootActorId,
        target: moreNodeId,
        edgeTypeId,
        privs: {},
        isMore: true,
        name: '',
        targetArrow: 'triangle',
      },
      status: 'new',
    };
    for (const node of hiddenNodes) {
      const edge = nodeToEdge[node.id];
      edge.data.source = moreNodeId;
      edge.data.sourceActorId = moreNodeId;
    }
    nodes.push(moreNode);
    edges.push(moreEdge);
    nextNodes.push(moreNodeId);
  }
  if (!nextNodes.length) return;
  for (const nodeId of nextNodes) {
    yield delay(1); // fix for Chrome TODO
    yield collapseNodesToMore({
      rootActorId: nodeId,
      nodes,
      edges,
      edgeTypeId,
      level,
    });
  }
}

/**
 * Исключить узлы без доступа + их связи
 */
export function excludeAccessDenied({ nodes = [], edges = [] }) {
  const copyNodes = nodes.filter((n) => !n.accessDenied);
  const copyEdges = edges.filter((n) => {
    const sourceNode = nodes.find((item) => item.id === n.source);
    const targetNode = nodes.find((item) => item.id === n.target);
    return (
      sourceNode &&
      targetNode &&
      !sourceNode.accessDenied &&
      !targetNode.accessDenied
    );
  });
  return { nodes: copyNodes, edges: copyEdges };
}

/**
 * Сформировать узлы для автослоя
 */
function* makeAutoLayerEls(id, root, list, total) {
  const rootNode = {
    id,
    status: 'new',
    data: {
      id,
      actorId: id,
      title: Utils.toPascalCase(id),
      type: 'node',
      privs: {},
      isMore: true,
      isAutoLayerRoot: true,
      isAutoLayerNode: true,
      ...root,
    },
  };
  switch (id) {
    case 'graphs':
      rootNode.data = {
        ...rootNode.data,
        graphsCount: total,
        pictureUrl: '/static/graph_node.svg',
      };
      break;
    case 'actors_bag':
      rootNode.data = {
        ...rootNode.data,
        tplsCount: total,
        pictureUrl: '/static/actor_gray.svg',
      };
      break;
    case 'actor_filter':
      rootNode.data = {
        ...rootNode.data,
        actorsCount: total,
        pictureUrl:
          root && root.color
            ? '/static/actor_white.svg'
            : '/static/actor_gray.svg',
      };
      break;
    default:
      break;
  }
  if (!list.length) return { nodes: [rootNode], edges: [] };
  const nodes = list.map((i) => ({
    id: i.id,
    status: 'new',
    data: {
      id: i.id,
      group: 'nodes',
      actorId: i.id,
      title: i.name,
      readOnly: !i.privs || !i.privs.modify,
      ...i,
      type: 'node',
      isTemplate: id === 'actors_bag',
      isAutoLayerNode: true,
    },
    position: null,
  }));
  const edgeType = 'hierarchy';
  const edgeTypes = yield select((state) => state.edgeTypes);
  const edgeTypeH = edgeTypes.find((i) => i.name === edgeType);
  const edges = list.map((i) => ({
    id: `edge_${i.id}`,
    status: 'new',
    data: {
      id: `edge_${i.id}`,
      type: 'edge',
      group: 'edges',
      edgeTypeId: edgeTypeH.id,
      edgeType,
      edgeId: `edge_${i.id}`,
      source: rootNode.id,
      target: i.id,
      title: '',
      privs: i.privs,
    },
  }));

  return { nodes: [rootNode, ...nodes], edges };
}

/**
 * Сформировать авто-слой (для графов или мешка акторов)
 */
export function* makeAutoLayerModel({ id, root, list, total }) {
  const { nodes, edges } = yield call(makeAutoLayerEls, id, root, list, total);
  nodes[0].replaceAll = true;
  return {
    id,
    title: Utils.toPascalCase(id),
    type: 'graph',
    isAuto: true,
    nodes,
    edges,
    privs: { view: true, modify: true, remove: true },
  };
}

/**
 * Сформировать из списка графов модаль графа
 */
export function* updateAutoLayerEls({ layerId, id, status, data }) {
  const copyNodes = yield call(getGraphEls, 'nodes');
  updateElProps(copyNodes, id, { status }, data);
  yield sendReducerMsg({
    layerId,
    type: UPDATE_ACTOR.SUCCESS,
    payload: { nodes: copyNodes },
  });
}

/**
 * Проверка на активный Stream
 */
export function* isActiveTemplateList(model) {
  const actorsList = yield select((state) => state.actorsList);
  const { formId, filter } = actorsList;
  const realFilter = {};
  Object.keys(filter || {}).forEach((key) => {
    if (!NON_FILTER_KEYS.includes(key)) realFilter[key] = filter[key];
  });
  return model.formId === formId && AppUtils.isEmptyObject(realFilter);
}
